import {
  deleteRequest,
  getBlobRequest,
  getRequest,
  patchRequest,
  postRawRequest,
  postRequest,
  postRequestMulitiPart,
  putRequest,
} from "./NetworkService";
import { Apinames } from "../utils/Apiname";
import {
  AcesCatalogue,
  AcesEdition,
  AnalysisListItem,
  AnalysisRegisterItem,
  AnalysisStatus,
  AnalysisView,
  AuditEntry,
  CapaAction,
  CapaDetail,
  CapaListItem,
  DashboardSummary,
  EffectiveAiSettings,
  EffectivenessResult,
  FindingAction,
  FindingDetail,
  FindingSummary,
  LovItem,
  Permission,
  PreAnalysisView,
  ReportListItem,
  ShipmentInfo,
  Site,
} from "../types/ai";

const result = async <T,>(p: Promise<{ data: any }>): Promise<T> => (await p).data.result as T;

const query = (params: Record<string, any>) => {
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return q ? `?${q}` : "";
};

/** Human-readable message from any API error (postRequest throws the lowercased message string on 400). */
export const apiError = (error: any, fallback = "Something went wrong."): string => {
  if (typeof error === "string") return error.charAt(0).toUpperCase() + error.slice(1);
  const msg = error?.response?.data?.result;
  if (typeof msg === "string") return msg.charAt(0).toUpperCase() + msg.slice(1);
  return fallback;
};

export const apiStatus = (error: any): number | undefined => error?.response?.status;

// ---- permissions & settings
export const getMyPermissions = () => result<Permission[]>(getRequest(Apinames.permissionsMe));
export const getUserPermissions = (userId: number) =>
  result<{ permission: Permission; granted: boolean; roleDefault: boolean }[]>(getRequest(Apinames.userPermissions(userId)));
export const updateUserPermissions = (userId: number, permissions: Partial<Record<Permission, boolean>>, reason?: string) =>
  result<{ permission: Permission; granted: boolean; roleDefault: boolean }[]>(
    putRequest(Apinames.userPermissions(userId), { permissions, reason })
  );
export const getAiSettings = () => result<EffectiveAiSettings>(getRequest(Apinames.aiSettings));

// ---- context
export const getSites = () => result<Site[]>(getRequest(Apinames.sites));
export const saveSite = (site: Partial<Site>) =>
  site.id ? result<Site>(putRequest(Apinames.site(site.id), site)) : result<Site>(postRequest(Apinames.sites, site));
export const getProcesses = () => result<LovItem[]>(getRequest(Apinames.lovProcesses));
export const getOperationalAreas = () => result<LovItem[]>(getRequest(Apinames.lovOperationalAreas));
export const addLov = (type: "PROCESS" | "OPERATIONAL_AREA", value: string) => result<LovItem>(postRequest(Apinames.lovAdd(type), { value }));
export const deleteLov = (id: number) => result<string>(deleteRequest(Apinames.lovItem(id), {}));
export const getActiveCatalogue = () => result<AcesCatalogue | null>(getRequest(Apinames.acesActive));

export interface AnalysisMetadata {
  siteId: number | null;
  operationalArea: string | null;
  process: string | null;
  temperatureMin: number | null;
  temperatureMax: number | null;
  shipment: ShipmentInfo | null;
  reason?: string;
}
export const updateAnalysisMetadata = (checklistId: number, body: AnalysisMetadata) =>
  result<any>(putRequest(Apinames.analysisMetadata(checklistId), body));

// ---- analyses
export const getPreAnalysis = (checklistId: number) => result<PreAnalysisView>(getRequest(Apinames.preAnalysis(checklistId)));
export const startAnalysis = (checklistId: number, language: string) =>
  result<AnalysisView>(postRequest(Apinames.analyses, { checklistId, language }));
export const analysisRegister = (params: { status?: AnalysisStatus; search?: string; page: number; size: number }) =>
  result<{ items: AnalysisRegisterItem[]; total: number; page: number; size: number }>(getRequest(`${Apinames.analyses}/register${query(params)}`));
export const getDashboardSummary = () => result<DashboardSummary>(getRequest("/dashboard/summary"));
export const getAnalysis = (id: number) => result<AnalysisView>(getRequest(Apinames.analysis(id)));
export const listAnalyses = (checklistId: number) => result<AnalysisListItem[]>(getRequest(Apinames.analyses + query({ checklistId })));
export const completeAnalysisReview = (id: number, reason?: string) =>
  result<AnalysisView>(postRequest(`${Apinames.analysis(id)}/complete-review`, { reason }));
export const retryAnalysis = (id: number) => result<AnalysisView>(postRequest(Apinames.analysisRetry(id), {}));
export const regenerateAnalysis = (id: number, reason: string, language?: string) =>
  result<AnalysisView>(postRequest(Apinames.analysisRegenerate(id), { reason, language }));

// ---- findings
export const listFindings = (params: { analysisId?: number; status?: string; classification?: string; riskLevel?: string; urgent?: boolean }) =>
  result<FindingSummary[]>(getRequest(Apinames.findings + query(params)));
export const getFinding = (id: number) => result<FindingDetail>(getRequest(Apinames.finding(id)));
export const updateFinding = (id: number, changes: Record<string, any>, reason: string, version: number) =>
  result<FindingDetail>(patchRequest(Apinames.finding(id), { changes, reason, version }));
export const transitionFinding = (id: number, action: FindingAction, reason?: string) =>
  result<FindingDetail>(postRequest(Apinames.findingTransition(id), { action, reason }));
export const commentFinding = (id: number, text: string) => result<any>(postRequest(Apinames.findingComments(id), { text }));

// ---- CAPA
export const createCapa = (findingId: number, mode: "FROM_AI" | "MANUAL") => result<CapaDetail>(postRequest(Apinames.capas, { findingId, mode }));
export const listCapas = (params: { status?: string; responsiblePersonId?: number; overdue?: boolean; urgent?: boolean; mine?: boolean }) =>
  result<CapaListItem[]>(getRequest(Apinames.capas + query(params)));
export const getCapa = (id: number) => result<CapaDetail>(getRequest(Apinames.capa(id)));
export const updateCapa = (id: number, changes: Record<string, any>, reason: string | undefined, version: number) =>
  result<CapaDetail>(patchRequest(Apinames.capa(id), { changes, reason, version }));
export const transitionCapa = (id: number, action: CapaAction, extra: { reason?: string; effectivenessResult?: EffectivenessResult; effectivenessNotes?: string } = {}) =>
  result<CapaDetail>(postRequest(Apinames.capaTransition(id), { action, ...extra }));
export const validateRootCause = (id: number, text: string, reason?: string) =>
  result<CapaDetail>(postRequest(Apinames.capaRootCause(id), { text, reason }));
export const uploadCapaEvidence = (id: number, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return result<CapaDetail>(postRequestMulitiPart(Apinames.capaEvidence(id), form));
};

// ---- reports
export const listReports = (analysisId: number) => result<ReportListItem[]>(getRequest(Apinames.analysisReports(analysisId)));
export const generateReport = (analysisId: number, type: "DRAFT" | "FINAL", language: string) =>
  result<ReportListItem>(postRequest(Apinames.analysisReports(analysisId), { type, language }));

// ---- files
export const fetchFile = async (fileId: number): Promise<Blob> => (await getBlobRequest(Apinames.file(fileId))).data as Blob;

// ---- audit
export const getEntityAudit = (entityType: string, entityId: number) =>
  result<AuditEntry[]>(getRequest(Apinames.auditEntity + query({ entityType, entityId })));
export const searchAudit = (params: { entityType?: string; entityId?: number; userId?: number; from?: string; to?: string; page?: number; size?: number }) =>
  result<{ items: AuditEntry[]; total: number; page: number; size: number }>(getRequest(Apinames.audit + query(params)));
export const exportAudit = async (params: { entityType?: string; from?: string; to?: string }): Promise<Blob> =>
  (await getBlobRequest(Apinames.auditExport + query(params))).data as Blob;

// ---- platform administration (SUPER_ADMIN)
export const listEditions = () => result<AcesEdition[]>(getRequest(Apinames.acesEditions));
export const createEdition = (code: string, title: string, effectiveDate?: string) =>
  result<AcesEdition>(postRequest(Apinames.acesEditions, { code, title, effectiveDate: effectiveDate || null }));
export const setEditionStatus = (id: number, status: AcesEdition["status"]) => result<AcesEdition>(putRequest(Apinames.acesEditionStatus(id), { status }));
export const getCatalogue = (id: number) => result<AcesCatalogue>(getRequest(Apinames.acesEdition(id)));
export const importCatalogue = (id: number, body: string, kind: "json" | "csv") =>
  result<AcesCatalogue>(postRawRequest(Apinames.acesImport(id), body, kind === "json" ? "application/json" : "text/csv"));
export const exportCatalogue = async (id: number): Promise<Blob> => (await getBlobRequest(Apinames.acesExport(id))).data as Blob;
export const updateClause = (id: number, changes: { title?: string; requirementSummary?: string; active?: boolean }) =>
  result<any>(putRequest(Apinames.acesClause(id), changes));
export const listTenantAiSettings = () =>
  result<{ tenantId: number; company: string | null; email: string; settings: EffectiveAiSettings; tokensUsedThisMonth: number }[]>(getRequest(Apinames.tenantAiSettings));
export const updateTenantAiSettings = (tenantId: number, body: Partial<{ aiEnabled: boolean; acesEditionId: number; clearAcesEdition: boolean; monthlyTokenBudget: number; maxImagesPerAnalysis: number }>) =>
  result<EffectiveAiSettings>(putRequest(Apinames.tenantAiSetting(tenantId), body));
const LATEST_BATCH = 200;
export const latestAnalyses = async (checklistIds: number[]) => {
  const batches: number[][] = [];
  for (let i = 0; i < checklistIds.length; i += LATEST_BATCH) batches.push(checklistIds.slice(i, i + LATEST_BATCH));
  const parts = await Promise.all(batches.map((ids) => result<Record<string, AnalysisListItem>>(getRequest(`/ai/analyses/latest?checklistIds=${ids.join(",")}`))));
  return Object.assign({}, ...parts) as Record<string, AnalysisListItem>;
};
export const capaAssignees = () => result<import("../types/ai").UserRef[]>(getRequest("/capas/assignees"));
